/**
 * Music player UI: minimized pill (play/pause, title, expand) and an expanded panel with
 * transport, progress/seek, ambient-break status, separate music and environment mixing,
 * shuffle/repeat, queue, track source, sleep timer and audio settings.
 */
import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import type { AppController } from './controller';
import { IconButton, Segmented, Slider, Toggle } from './ui/controls';

function fmt(t: number): string {
  if (!Number.isFinite(t) || t < 0) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function useNow(active: boolean, ms = 500): number {
  const [n, setN] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => setN(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [active, ms]);
  return n;
}

const SLEEP_CHOICES = [15, 30, 45, 60, 90];

export function Player({ app, chromeVisible }: { app: AppController; chromeVisible: boolean }) {
  const music = app.music;
  const s = useSyncExternalStore(music.subscribe, music.getSnapshot);
  const audioState = useSyncExternalStore(
    (fn) => app.audio.onState(fn),
    () => app.audio.state,
  );
  const prefs = useSyncExternalStore(app.prefs.subscribe, () => app.prefs.get());
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'main' | 'queue' | 'settings'>('main');
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const isPlaying = s.status === 'playing' || s.status === 'loading';
  const inBreak = s.status === 'break';
  const now = useNow(open && (isPlaying || s.sleepEndsAt !== null));
  const [scrub, setScrub] = useState<number | null>(null);
  const position = scrub ?? music.currentTime();
  const duration = music.duration();
  const a = prefs.audio;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const title = s.track?.title ?? 'Music';
  const label = inBreak ? 'Listening to the world…' : s.status === 'idle' ? `${title}` : title;
  const updateAudio = (fn: (d: typeof a) => void) => app.prefs.update((d) => fn(d.audio));

  return (
    <div ref={rootRef} className={`gbr-player gbr-surface ${open ? '' : 'gbr-chrome'}`} data-open={open} aria-label="Music player" role="region" style={!open && !chromeVisible ? undefined : undefined}>
      {!open && (
        <div className="gbr-player-pill">
          {audioState !== 'running' && (
            <IconButton icon="volumeOff" label="Sound is off. Turn on the world’s sound" onClick={() => music.ensureAudio()} />
          )}
          <IconButton icon={isPlaying || inBreak ? 'pause' : 'play'} label={isPlaying || inBreak ? 'Pause music' : 'Play music'} onClick={() => music.toggle()} />
          <span className="gbr-player-title" aria-live="off">
            {label}
          </span>
          <IconButton icon="chevronUp" label="Expand music player" expanded={false} controls={panelId} onClick={() => setOpen(true)} />
        </div>
      )}
      {open && (
        <div id={panelId} className="gbr-player-panel">
          <div className="gbr-player-head">
            <div className="gbr-player-now">
              <strong>{inBreak ? 'Listening to the world…' : title}</strong>
              <span>{inBreak ? `Up next: ${s.queue[s.nextIndex]?.title ?? ''}` : s.track ? s.track.artist : ''}</span>
            </div>
            <div className="gbr-row">
              <IconButton icon="queue" label="Playlist" pressed={view === 'queue'} onClick={() => setView(view === 'queue' ? 'main' : 'queue')} />
              <IconButton icon="gear" label="Audio settings" pressed={view === 'settings'} onClick={() => setView(view === 'settings' ? 'main' : 'settings')} />
              <IconButton icon="chevronDown" label="Minimize music player" expanded controls={panelId} onClick={() => setOpen(false)} />
            </div>
          </div>

          {inBreak ? (
            <div className="gbr-break">
              <span>A quiet pause before the next piece.</span>
              <button type="button" className="gbr-btn gbr-btn--chip" onClick={() => music.player.playNextNow()}>
                Play next now
              </button>
            </div>
          ) : (
            <div className="gbr-time-row">
              <span>{fmt(position)}</span>
              <Slider
                label="Track position"
                min={0}
                max={Math.max(1, duration)}
                step={1}
                value={Math.min(position, Math.max(1, duration))}
                disabled={!duration || (s.status !== 'playing' && s.status !== 'paused')}
                format={(v) => `${fmt(v)} of ${fmt(duration)}`}
                onChange={(v) => {
                  setScrub(v);
                  music.player.seek(v);
                  window.setTimeout(() => setScrub(null), 200);
                }}
              />
              <span>{fmt(duration)}</span>
            </div>
          )}

          <div className="gbr-transport">
            <IconButton icon="shuffle" label="Shuffle" pressed={s.shuffle} onClick={() => updateAudio((d) => (d.shuffle = !d.shuffle))} size="sm" />
            <IconButton icon="previous" label="Previous track" onClick={() => music.player.previous()} />
            <IconButton icon={isPlaying || inBreak ? 'pause' : 'play'} label={isPlaying || inBreak ? 'Pause music' : 'Play music'} variant="solid" size="lg" onClick={() => music.toggle()} />
            <IconButton icon="next" label="Next track" onClick={() => music.player.next()} />
            <IconButton
              icon={s.repeat === 'one' ? 'repeatOne' : 'repeat'}
              label={s.repeat === 'off' ? 'Repeat: off' : s.repeat === 'all' ? 'Repeat: playlist' : 'Repeat: this track'}
              pressed={s.repeat !== 'off'}
              size="sm"
              onClick={() => updateAudio((d) => (d.repeat = d.repeat === 'off' ? 'all' : d.repeat === 'all' ? 'one' : 'off'))}
            />
          </div>

          {view === 'main' && (
            <>
              <div className="gbr-mix-row">
                <span>Music</span>
                <IconButton icon={a.musicMuted ? 'volumeOff' : 'volume'} label={a.musicMuted ? 'Unmute music' : 'Mute music'} pressed={a.musicMuted} size="sm" onClick={() => updateAudio((d) => (d.musicMuted = !d.musicMuted))} />
                <Slider label="Music volume" value={a.musicVolume} onChange={(v) => updateAudio((d) => (d.musicVolume = v))} />
              </div>
              <div className="gbr-mix-row">
                <span>Environment</span>
                <IconButton icon={a.envMuted ? 'volumeOff' : 'volume'} label={a.envMuted ? 'Unmute environment' : 'Mute environment'} pressed={a.envMuted} size="sm" onClick={() => updateAudio((d) => (d.envMuted = !d.envMuted))} />
                <Slider label="Environment volume" value={a.envVolume} onChange={(v) => updateAudio((d) => (d.envVolume = v))} />
              </div>
              <div className="gbr-divider" />
              <div className="gbr-section">
                <h3>Sleep timer</h3>
                <div className="gbr-chips" role="group" aria-label="Sleep timer">
                  {SLEEP_CHOICES.map((m) => (
                    <button key={m} type="button" className="gbr-btn gbr-btn--chip" aria-pressed={false} onClick={() => music.player.setSleepTimer(m, a.sleepMusicOnly)}>
                      {m} min
                    </button>
                  ))}
                  {s.sleepEndsAt !== null && (
                    <button type="button" className="gbr-btn gbr-btn--chip" aria-pressed onClick={() => music.player.setSleepTimer(null, a.sleepMusicOnly)}>
                      Cancel · {Math.max(0, Math.ceil((s.sleepEndsAt - now) / 60000))} min left
                    </button>
                  )}
                </div>
                <span className="gbr-hint">{a.sleepMusicOnly ? 'Gently fades the music, then pauses it.' : 'Gently fades music and the world’s sound, then pauses.'}</span>
              </div>
            </>
          )}

          {view === 'queue' && (
            <div className="gbr-section">
              <h3>{s.queue.length} pieces</h3>
              <ol className="gbr-queue">
                {s.queue.map((t, i) => (
                  <li key={`${t.id}-${i}`}>
                    <button type="button" aria-current={i === s.index} onClick={() => {
                        music.ensureAudio();
                        music.player.selectTrack(i);
                      }}>
                      <span>{t.title}</span>
                      <small>{i === s.index ? 'Now' : i === s.nextIndex ? 'Next' : ''}</small>
                    </button>
                  </li>
                ))}
              </ol>
              {s.track && <span className="gbr-hint">Source: {s.track.source}</span>}
            </div>
          )}

          {view === 'settings' && (
            <div className="gbr-section">
              <Toggle label="Ambient breaks" description="Rest 20–60 seconds between pieces and let the world be heard." checked={a.ambientBreaks} onChange={(v) => updateAudio((d) => (d.ambientBreaks = v))} />
              <Toggle label="Keep current music on world change" checked={a.keepMusicOnWorldChange} onChange={(v) => updateAudio((d) => (d.keepMusicOnWorldChange = v))} />
              <Toggle label="Spatial ambience" description="Place drips and rustles gently left or right." checked={a.spatial} onChange={(v) => updateAudio((d) => (d.spatial = v))} />
              <Toggle label="Sleep timer fades music only" checked={a.sleepMusicOnly} onChange={(v) => updateAudio((d) => (d.sleepMusicOnly = v))} />
              <Toggle label="Keep playing in the background" description="Sound continues when this tab is hidden, where the browser allows." checked={a.playWhenHidden} onChange={(v) => updateAudio((d) => (d.playWhenHidden = v))} />
              <Segmented
                label="Repeat"
                value={a.repeat}
                options={[
                  { value: 'off', label: 'Once' },
                  { value: 'all', label: 'Playlist' },
                  { value: 'one', label: 'This piece' },
                ]}
                onChange={(v) => updateAudio((d) => (d.repeat = v))}
              />
            </div>
          )}
          {s.error === 'blocked' && <span className="gbr-hint">Press play to start sound — your browser waits for a tap.</span>}
          {s.error === 'unavailable' && <span className="gbr-hint">This piece could not be loaded; moving on shortly.</span>}
        </div>
      )}
    </div>
  );
}
